import React, { useMemo } from "react";
import type { CalendarEventType, SlotInfo } from "../../../../types/calendar";
import type { CalendarDropInfo } from "../components/Views/DnDCalendar";
import {
  eventsWithDraft,
  isTempEvent,
  notResizable,
} from "./bookingTempEvents";
import type { BookingSession } from "./useBookingSession";

interface CalendarHandlers {
  events: CalendarEventType[];
  onSelectEvent: (event: CalendarEventType, e?: React.SyntheticEvent) => void;
  onDoubleClickEvent: (event: CalendarEventType) => void;
  onSelectSlot: (slotInfo: SlotInfo) => void;
  onEventDrop: (info: CalendarDropInfo) => void;
}

export interface CalendarViewProps extends CalendarHandlers {
  draggableAccessor?: (event: CalendarEventType) => boolean;
  resizableAccessor?: (event: CalendarEventType) => boolean;
  scrollTimeOfDay?: string | null;
}

/**
 * The Day/Week view props. While the booking panel is open the calendar shows
 * the draft's temp events (a reschedule hides its source appointment's
 * events, so the temp events replace them in place), only those are
 * draggable, nothing resizes, and every interaction routes through the
 * booking session (event click → routeCalendarEventClick, slot → move the
 * active row, drop → move that row). Closed → the normal calendar handlers.
 * All handlers are the callers' stable callbacks, so the object only changes
 * when an input does.
 */
export function useBookingViewProps(
  booking: BookingSession,
  normal: CalendarHandlers,
): CalendarViewProps {
  const {
    panelOpen,
    tempEvents,
    hiddenTransactionId,
    scrollTimeOfDay,
    handleCalendarSelectEvent,
    handleCalendarSelectSlot,
    handleCalendarDrop,
  } = booking;
  const {
    events,
    onSelectEvent,
    onDoubleClickEvent,
    onSelectSlot,
    onEventDrop,
  } = normal;

  const bookingEvents = useMemo(
    () =>
      panelOpen
        ? eventsWithDraft(events, tempEvents, hiddenTransactionId)
        : events,
    [panelOpen, events, tempEvents, hiddenTransactionId],
  );

  return useMemo<CalendarViewProps>(
    () =>
      panelOpen
        ? {
            events: bookingEvents,
            onSelectEvent: handleCalendarSelectEvent,
            onDoubleClickEvent: handleCalendarSelectEvent,
            onSelectSlot: handleCalendarSelectSlot,
            onEventDrop: handleCalendarDrop,
            draggableAccessor: isTempEvent,
            resizableAccessor: notResizable,
            scrollTimeOfDay,
          }
        : {
            events: bookingEvents,
            onSelectEvent,
            onDoubleClickEvent,
            onSelectSlot,
            onEventDrop,
          },
    [
      panelOpen,
      bookingEvents,
      handleCalendarSelectEvent,
      handleCalendarSelectSlot,
      handleCalendarDrop,
      scrollTimeOfDay,
      onSelectEvent,
      onDoubleClickEvent,
      onSelectSlot,
      onEventDrop,
    ],
  );
}
